import React, { FC, useEffect, useContext } from 'react'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import ManageContext from '../contexts/manage'
import useManage from '../hooks/useManage'
import ManageTabs from '../components/manage/Tabs'
import useFullRoute from '../hooks/useFullRoute'

const ManageLayoutContent: FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const { fullRoute } = useFullRoute()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const manage = useContext(ManageContext)

  return (
    <Container maxWidth="lg">
      <Box sx={{
        mt: 2,
        mb: 4,
        width: '100%',
        p: 0,
      }}>
        <div>
          {fullRoute.meta?.showTabs && (
            <>
              {isMobile && (
                <Box sx={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e5e5',
                  width: '100%',
                  mb: 2,
                  '@media (min-width: 900px)': {
                    width: '300px',
                    mr: 2,
                    mb: 0
                  }
                }}>
                  <ManageTabs data={manage.data?.booking} />
                </Box>
              )}
              <Box sx={{
                flexGrow: 1,
                width: '100%'
              }}>
                {manage.alerts.map((alert, index) => (
                  <Alert key={index} severity="info" sx={{ mb: 2 }}>
                    <AlertTitle>{alert.title}</AlertTitle>
                    {alert.message}
                  </Alert>
                ))}
                <div>
                  {isMobile ? (
                    <Box sx={{
                      borderRadius: '5px',
                      p: 3,
                      border: '1px solid #e5e5e5',
                      backgroundColor: '#fff',
                    }}>
                      {children}
                    </Box>
                  ) : (
                    <Box sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                    }}>
                      <Box sx={{
                        width: '300px',
                        minWidth: '300px',
                        flexGrow: 0,
                      }}>
                        <Box sx={{
                          borderRadius: '5px',
                          border: '1px solid #e5e5e5',
                          backgroundColor: '#fff',
                        }}>
                          <ManageTabs data={manage.data?.booking} />
                        </Box>
                      </Box>
                      <Box sx={{
                        flexGrow: 1,
                        pl: 3,
                      }}>
                        <Box sx={{
                          borderRadius: '5px',
                          p: 3,
                          border: '1px solid #e5e5e5',
                          backgroundColor: '#fff',
                        }}>
                          {children}
                        </Box>
                      </Box>
                    </Box>
                  )}
                </div>
              </Box>
            </>
          )}
          {!fullRoute.meta?.showTabs && (
            <Box sx={{
              flexGrow: 1,
              width: '100%',
            }}>
              {manage.alerts.map((alert, index) => (
                <Alert key={index} severity="info" sx={{ mb: 2 }}>
                  <AlertTitle>{alert.title}</AlertTitle>
                  {alert.message}
                </Alert>
              ))}
              <Box sx={{
                borderRadius: '5px',
                p: 3,
                border: '1px solid #e5e5e5',
                backgroundColor: '#fff',
              }}>
                {children}
              </Box>
            </Box>
          )}
        </div>
        {manage.generalConfig && (manage.generalConfig.contact_email || manage.generalConfig.contact_phone) && (
          <Paper sx={{
            borderRadius: '5px',
            p: 3,
            border: '1px solid #e5e5e5',
            backgroundColor: '#fff',
            mt: 2,
          }}>
            <Typography variant="h6" gutterBottom>
              Get in touch
            </Typography>
            <Typography variant="body1" gutterBottom>
              If you are having any problems or want to get in touch with us for another reason, please use the following details:
            </Typography>
            <Typography variant="body1" gutterBottom component="div">
              <ul>
                {manage.generalConfig.contact_email && (
                  <li>
                    <strong>Email: </strong> {manage.generalConfig.contact_email}
                  </li>
                )}
                {manage.generalConfig.contact_phone && (
                  <li>
                    <strong>Phone: </strong> {manage.generalConfig.contact_phone}
                  </li>
                )}
              </ul>
            </Typography>
          </Paper>
        )}
      </Box>
    </Container>
  )
}

const ManageLayout: FC<React.PropsWithChildren<{
  compact?: boolean,
}>> = ({
  children,
  compact = false,
}) => {
  const manage = useManage()

  useEffect(() => {
    if (manage.initialised) {
      window.dispatchEvent(new Event('resize'))
    }
  }, [manage.initialised])

  if (!manage.initialised) {
    return (
      <Container maxWidth="lg">
        <Box sx={{
          mt: 2,
          mb: 4,
          width: '100%',
          p: 0,
        }}>
          <Box sx={{
            borderRadius: '5px',
            p: 3,
            border: '1px solid #e5e5e5',
            backgroundColor: '#fff',
          }}>
            <Box display="flex" justifyContent="center" p={2}>
              Loading...
            </Box>
          </Box>
        </Box>
      </Container>
    )
  }

  return (
    <ManageContext.Provider value={manage}>
      <ManageLayoutContent>
        {children}
      </ManageLayoutContent>
    </ManageContext.Provider>
  )
}

export default ManageLayout